import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../contexts/AuthContext';
import { db } from '../utils/firebaseConfig';
import {
  collection,
  query,
  where,
  getDocs,
  deleteDoc,
  doc,
  orderBy,
  limit,
  startAfter,
  getDoc
} from 'firebase/firestore';
import { toast } from 'react-toastify';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FiSearch,
  FiTrash2,
  FiFileText,
  FiCalendar,
  FiUser,
  FiHash,
  FiClock,
  FiChevronLeft,
  FiChevronRight,
  FiAlertTriangle,
  FiFilter,
  FiX
} from 'react-icons/fi';
import { format } from 'date-fns';
import ConfirmationModal from './ConfirmationModal';

const ManageDORs = () => {
  const { role, currentUser } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [reports, setReports] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTrainee, setSelectedTrainee] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedReportType, setSelectedReportType] = useState('');
  const [trainees, setTrainees] = useState([]);
  const [lastDoc, setLastDoc] = useState({});
  const [hasMore, setHasMore] = useState({});
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
  });
  const [deletingId, setDeletingId] = useState(null);
  const [pinModal, setPinModal] = useState({
    isOpen: false,
    onSuccess: () => {},
  });
  const [pinInput, setPinInput] = useState('');
  const [pinError, setPinError] = useState('');
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const [isLocked, setIsLocked] = useState(false);

  // Super admin PIN - in a real app, this would be in environment variables or secure storage
  const SUPER_ADMIN_PIN = '6401';

  const pageSize = 20;
  const maxAttempts = 3;
  const lockoutDuration = 15 * 60 * 1000; // 15 minutes

  // PIN verification functions
  const verifyPin = (pin) => {
    if (isLocked) {
      setPinError('Access locked. Please try again later.');
      return false;
    }

    if (pin === SUPER_ADMIN_PIN) {
      setIsAuthorized(true);
      setAttempts(0);
      setPinError('');
      setPinInput('');
      setPinModal({ isOpen: false, onSuccess: () => {} });
      toast.success('Super admin access granted');
      return true;
    } else {
      const newAttempts = attempts + 1;
      setAttempts(newAttempts);
      
      if (newAttempts >= maxAttempts) {
        setIsLocked(true);
        setPinError(`Too many failed attempts. Access locked for 15 minutes.`);
        toast.error('Access locked due to multiple failed attempts');
        
        // Auto-unlock after lockout duration
        setTimeout(() => {
          setIsLocked(false);
          setAttempts(0);
          setPinError('');
        }, lockoutDuration);
      } else {
        setPinError(`Incorrect PIN. ${maxAttempts - newAttempts} attempts remaining.`);
      }
      return false;
    }
  };

  const requestPinAccess = (onSuccess) => {
    if (isAuthorized) {
      onSuccess();
      return;
    }
    
    setPinModal({
      isOpen: true,
      onSuccess
    });
  };

  const handlePinSubmit = (e) => {
    e.preventDefault();
    if (verifyPin(pinInput)) {
      pinModal.onSuccess();
    }
  };

  // Auto-logout after 30 minutes of inactivity
  useEffect(() => {
    if (isAuthorized) {
      const timeout = setTimeout(() => {
        setIsAuthorized(false);
        toast.info('Super admin session expired');
      }, 30 * 60 * 1000); // 30 minutes

      return () => clearTimeout(timeout);
    }
  }, [isAuthorized]);

  // Report type configurations
  const reportTypes = {
    DOR: {
      collection: 'dors',
      name: 'Daily Observation Report',
      dateField: 'date',
      icon: FiFileText,
      color: 'blue'
    },
    EOWR: {
      collection: 'endOfWeekReports',
      name: 'End of Week Report',
      dateField: 'weekEndingDate',
      icon: FiCalendar,
      color: 'green'
    },
    OSR: {
      collection: 'officerSafetyReports',
      name: 'Officer Safety Report',
      dateField: 'reportDate',
      icon: FiAlertTriangle,
      color: 'red'
    }
  };

  // Load trainees for filter dropdown
  useEffect(() => {
    const loadTrainees = async () => {
      try {
        const q = query(
          collection(db, 'users'),
          where('role', '==', 'trainee'),
          orderBy('name')
        );
        const snapshot = await getDocs(q);
        const traineeList = snapshot.docs.map(doc => ({
          id: doc.id,
          name: doc.data().name || 'Unknown',
          idNumber: doc.data().idNumber || ''
        }));
        setTrainees(traineeList);
      } catch (error) {
        console.error('Error loading trainees:', error);
      }
    };
    loadTrainees();
  }, []);

  // Load Reports
  const loadReports = async (loadMore = false) => {
    setLoading(true);
    try {
      const allReports = [];
      const reportTypesToLoad = selectedReportType ? [selectedReportType] : Object.keys(reportTypes);

      for (const reportType of reportTypesToLoad) {
        const config = reportTypes[reportType];
        const filters = [];

        // Add filters
        if (selectedTrainee) {
          filters.push(where('traineeId', '==', selectedTrainee));
        }
        if (selectedDate) {
          const [year, month, day] = selectedDate.split('-').map(Number);
          const startDate = new Date(Date.UTC(year, month - 1, day));
          const endDate = new Date(Date.UTC(year, month - 1, day + 1));
          filters.push(where(config.dateField, '>=', startDate));
          filters.push(where(config.dateField, '<', endDate));
        }

        // Build query
        let q = query(
          collection(db, config.collection),
          ...filters,
          orderBy(config.dateField, 'desc'),
          limit(pageSize)
        );

        // Handle pagination
        if (loadMore && lastDoc[reportType]) {
          q = query(
            collection(db, config.collection),
            ...filters,
            orderBy(config.dateField, 'desc'),
            startAfter(lastDoc[reportType]),
            limit(pageSize)
          );
        }

        const snapshot = await getDocs(q);
        const reportList = snapshot.docs.map(doc => {
          const data = doc.data();
          // Convert Timestamp objects to dates and normalize field names
          return {
            id: doc.id,
            reportType,
            collection: config.collection,
            ...data,
            reportDate: data[config.dateField]?.toDate ? data[config.dateField].toDate() : data[config.dateField],
            submissionDate: data.submissionDate?.toDate ? data.submissionDate.toDate() : data.submissionDate,
            createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : data.createdAt,
            // Normalize report ID field
            reportId: data.reportId || data.weekReportId || data.safetyReportId || 'N/A'
          };
        });

        allReports.push(...reportList);

        // Update pagination state for this report type
        setLastDoc(prev => ({
          ...prev,
          [reportType]: snapshot.docs[snapshot.docs.length - 1]
        }));
        setHasMore(prev => ({
          ...prev,
          [reportType]: snapshot.docs.length === pageSize
        }));
      }

      // Sort all reports by date
      allReports.sort((a, b) => {
        const dateA = a.reportDate || a.createdAt || new Date(0);
        const dateB = b.reportDate || b.createdAt || new Date(0);
        return dateB - dateA;
      });

      // Filter by search term
      let filteredReports = allReports;
      if (searchTerm.trim()) {
        const term = searchTerm.toLowerCase();
        filteredReports = allReports.filter(report => 
          report.reportId?.toLowerCase().includes(term) ||
          report.traineeName?.toLowerCase().includes(term) ||
          report.trainerName?.toLowerCase().includes(term) ||
          reportTypes[report.reportType]?.name.toLowerCase().includes(term)
        );
      }

      if (loadMore) {
        setReports(prev => [...prev, ...filteredReports]);
      } else {
        setReports(filteredReports);
      }

    } catch (error) {
      console.error('Error loading reports:', error);
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReports();
  }, [selectedTrainee, selectedDate, selectedReportType]);

  // Handle search with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      loadReports();
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const deleteReport = async (reportId, reportType, displayId) => {
    const config = reportTypes[reportType];
    
    const performDelete = () => {
      setConfirmModal({
        isOpen: true,
        title: `Delete ${config.name}`,
        message: `Are you sure you want to delete ${config.name} ${displayId}? This action cannot be undone.`,
        onConfirm: async () => {
          setConfirmModal({ ...confirmModal, isOpen: false });
          setDeletingId(reportId);
          try {
            await deleteDoc(doc(db, config.collection, reportId));
            toast.success(`${config.name} deleted successfully`);
            // Remove from local state
            setReports(prev => prev.filter(r => r.id !== reportId));
          } catch (error) {
            console.error(`Error deleting ${config.name}:`, error);
            toast.error(`Failed to delete ${config.name}`);
          } finally {
            setDeletingId(null);
          }
        },
      });
    };

    // Request PIN verification before allowing delete
    requestPinAccess(performDelete);
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    try {
      // Handle Firestore Timestamp
      if (date && typeof date === 'object' && 'seconds' in date) {
        return format(new Date(date.seconds * 1000), 'MMM d, yyyy');
      }
      // Handle Date object or string
      const d = date instanceof Date ? date : new Date(date);
      if (isNaN(d.getTime())) return 'Invalid Date';
      return format(d, 'MMM d, yyyy');
    } catch (error) {
      console.error('Error formatting date:', error, date);
      return 'Invalid Date';
    }
  };

  const formatDateTime = (date) => {
    if (!date) return 'N/A';
    try {
      // Handle Firestore Timestamp
      if (date && typeof date === 'object' && 'seconds' in date) {
        return format(new Date(date.seconds * 1000), 'MMM d, yyyy h:mm a');
      }
      // Handle Date object or string
      const d = date instanceof Date ? date : new Date(date);
      if (isNaN(d.getTime())) return 'Invalid Date';
      return format(d, 'MMM d, yyyy h:mm a');
    } catch (error) {
      console.error('Error formatting datetime:', error, date);
      return 'Invalid Date';
    }
  };

  // Only admins can access this component
  if (role !== 'admin') {
    return (
      <motion.div 
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="p-6 flex items-center justify-center h-full"
      >
        <div className="bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200 p-4 rounded-lg shadow-md flex items-center">
          <FiAlertTriangle className="mr-2 flex-shrink-0" size={20} />
          <span>Access Denied - Admin privileges required</span>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
      className="min-h-screen p-4 sm:p-6 md:p-8 bg-background-light dark:bg-background-dark overflow-x-hidden"
    >
      <div className="w-full max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="mb-4 sm:mb-6"
        >
          <div className="flex items-start justify-between">
            <div className="flex-1 min-w-0">
              <h2 className="text-lg sm:text-xl font-bold flex items-center text-text-light dark:text-text-dark">
                <FiFileText className="mr-2 sm:mr-3 text-accent-light dark:text-accent-dark flex-shrink-0" size={20} />
                <span className="truncate">Manage Reports</span>
              </h2>
              <p className="text-xs sm:text-sm text-text-light/70 dark:text-text-dark/70 mt-1 sm:mt-2">
                Search, view, and delete all types of reports from the system
              </p>
            </div>
            
            {/* Security Status Indicator */}
            <div className="flex-shrink-0 ml-4">
              <div className={`inline-flex items-center px-2 sm:px-3 py-1 rounded-full text-xs font-medium ${
                isAuthorized 
                  ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200' 
                  : 'bg-gray-100 dark:bg-gray-900/30 text-gray-800 dark:text-gray-200'
              }`}>
                <div className={`w-2 h-2 rounded-full mr-1 sm:mr-2 ${
                  isAuthorized ? 'bg-green-500' : 'bg-gray-400'
                }`}></div>
                <span className="hidden sm:inline">
                  {isAuthorized ? 'Super Admin' : 'Protected'}
                </span>
                <span className="sm:hidden">
                  {isAuthorized ? 'Unlocked' : 'Locked'}
                </span>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Filters */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="bg-primary-light dark:bg-primary-dark p-2 sm:p-4 rounded-lg shadow-md border border-secondary-light dark:border-secondary-dark mb-4 sm:mb-6"
        >
          <div className="space-y-3 sm:space-y-4">
            {/* Search */}
            <div>
              <label className="block text-xs font-medium mb-1 sm:mb-2">
                <FiSearch className="inline mr-1 sm:mr-2" size={12} />
                Search
              </label>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Report ID, trainee, or trainer..."
                className="w-full p-2 sm:p-3 text-xs sm:text-sm rounded-md bg-secondary-light/30 dark:bg-secondary-dark border border-secondary-light dark:border-secondary-dark focus:ring-2 focus:ring-accent-light dark:focus:ring-accent-dark outline-none transition-colors"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
              {/* Report Type Filter */}
              <div>
                <label className="block text-xs font-medium mb-1 sm:mb-2">
                  <FiFilter className="inline mr-1 sm:mr-2" size={12} />
                  Report Type
                </label>
                <select
                  value={selectedReportType}
                  onChange={(e) => setSelectedReportType(e.target.value)}
                  className="w-full p-2 sm:p-3 text-xs sm:text-sm rounded-md bg-secondary-light/30 dark:bg-secondary-dark border border-secondary-light dark:border-secondary-dark focus:ring-2 focus:ring-accent-light dark:focus:ring-accent-dark outline-none transition-colors"
                >
                  <option value="">All Report Types</option>
                  {Object.entries(reportTypes).map(([key, config]) => (
                    <option key={key} value={key}>
                      {config.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Trainee Filter */}
              <div>
                <label className="block text-xs font-medium mb-1 sm:mb-2">
                  <FiUser className="inline mr-1 sm:mr-2" size={12} />
                  Trainee
                </label>
                <select
                  value={selectedTrainee}
                  onChange={(e) => setSelectedTrainee(e.target.value)}
                  className="w-full p-2 sm:p-3 text-xs sm:text-sm rounded-md bg-secondary-light/30 dark:bg-secondary-dark border border-secondary-light dark:border-secondary-dark focus:ring-2 focus:ring-accent-light dark:focus:ring-accent-dark outline-none transition-colors"
                >
                  <option value="">All Trainees</option>
                  {trainees.map(trainee => (
                    <option key={trainee.id} value={trainee.id}>
                      {trainee.name} {trainee.idNumber && `(#${trainee.idNumber})`}
                    </option>
                  ))}
                </select>
              </div>

              {/* Date Filter */}
              <div>
                <label className="block text-xs font-medium mb-1 sm:mb-2">
                  <FiCalendar className="inline mr-1 sm:mr-2" size={12} />
                  Date
                </label>
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  className="w-full p-2 sm:p-3 text-xs sm:text-sm rounded-md bg-secondary-light/30 dark:bg-secondary-dark border border-secondary-light dark:border-secondary-dark focus:ring-2 focus:ring-accent-light dark:focus:ring-accent-dark outline-none transition-colors"
                />
              </div>
            </div>

            {/* Clear Filters */}
            {(searchTerm || selectedTrainee || selectedDate || selectedReportType) && (
              <motion.button
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                onClick={() => {
                  setSearchTerm('');
                  setSelectedTrainee('');
                  setSelectedDate('');
                  setSelectedReportType('');
                }}
                className="text-xs sm:text-sm text-accent-light dark:text-accent-dark hover:underline flex items-center"
              >
                <FiX className="mr-1" size={14} />
                Clear all filters
              </motion.button>
            )}
          </div>
        </motion.div>

        {/* DORs List */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          {loading && reports.length === 0 ? (
            <div className="bg-primary-light dark:bg-primary-dark rounded-lg shadow-md border border-secondary-light dark:border-secondary-dark p-4 sm:p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 sm:h-12 sm:w-12 border-b-2 border-accent-light dark:border-accent-dark mx-auto"></div>
              <p className="mt-3 sm:mt-4 text-sm sm:text-base text-text-light/70 dark:text-text-dark/70">Loading reports...</p>
            </div>
          ) : reports.length === 0 ? (
            <div className="bg-primary-light dark:bg-primary-dark rounded-lg shadow-md border border-secondary-light dark:border-secondary-dark p-4 sm:p-8 text-center">
              <FiFileText className="mx-auto h-8 w-8 sm:h-12 sm:w-12 text-text-light/30 dark:text-text-dark/30" />
              <p className="mt-3 sm:mt-4 text-sm sm:text-base text-text-light/70 dark:text-text-dark/70">No reports found</p>
            </div>
          ) : (
            <>
              {/* Mobile Card View */}
              <div className="block sm:hidden space-y-3 w-full">
                <AnimatePresence>
                  {reports.map((report, index) => {
                    const config = reportTypes[report.reportType];
                    const IconComponent = config.icon;
                    const colorClass = {
                      blue: 'text-blue-600 dark:text-blue-400',
                      green: 'text-green-600 dark:text-green-400',
                      red: 'text-red-600 dark:text-red-400'
                    }[config.color];

                    return (
                      <motion.div
                        key={report.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                        transition={{ duration: 0.3, delay: index * 0.05 }}
                        className="bg-primary-light dark:bg-primary-dark rounded-lg shadow-md border border-secondary-light dark:border-secondary-dark p-2 sm:p-3 hover:shadow-lg transition-shadow w-full"
                      >
                        {/* Header with Report Type, ID and Delete Button */}
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex flex-wrap items-center flex-1 min-w-0 gap-x-1 gap-y-1 overflow-hidden">
                            <div className="flex items-center gap-1 flex-shrink-0">
                              <IconComponent className={`${colorClass}`} size={12} />
                              <span className="font-medium text-xs whitespace-nowrap">{config.name}</span>
                            </div>
                            <div className="flex items-center gap-1 min-w-0">
                              <FiHash className="text-text-light/50 dark:text-text-dark/50 flex-shrink-0" size={8} />
                              <span className="font-medium text-xs truncate">{report.reportId}</span>
                            </div>
                          </div>
                          <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => deleteReport(report.id, report.reportType, report.reportId)}
                            disabled={deletingId === report.id}
                            className="flex-shrink-0 ml-1 inline-flex items-center px-2 py-2 sm:px-1 sm:py-1 text-xs rounded-md sm:rounded bg-red-500/10 text-red-600 dark:text-red-400 hover:bg-red-500/20 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed min-h-[32px] sm:min-h-auto"
                          >
                            {deletingId === report.id ? (
                              <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-red-600 dark:border-red-400"></div>
                            ) : (
                              <FiTrash2 size={12} />
                            )}
                          </motion.button>
                        </div>

                        {/* Trainee Info */}
                        <div className="mb-2">
                          <div className="flex items-center text-xs font-medium text-text-light dark:text-text-dark">
                            <FiUser className="mr-1 text-text-light/50 dark:text-text-dark/50 flex-shrink-0" size={10} />
                            <span className="truncate">{report.traineeName || 'Unknown'}</span>
                            {report.traineeIdNumber && (
                              <span className="ml-1 text-xs text-text-light/60 dark:text-text-dark/60">#{report.traineeIdNumber}</span>
                            )}
                          </div>
                          <div className="text-xs text-text-light/70 dark:text-text-dark/70 ml-3 truncate">
                            Trainer: {report.trainerName || 'Unknown'}
                          </div>
                        </div>

                        {/* Dates */}
                        <div className="grid grid-cols-1 gap-1 text-xs text-text-light/70 dark:text-text-dark/70">
                          <div className="flex items-center">
                            <FiCalendar className="mr-1 flex-shrink-0" size={8} />
                            <span className="truncate">Report: {formatDate(report.reportDate)}</span>
                          </div>
                          <div className="flex items-center">
                            <FiClock className="mr-1 flex-shrink-0" size={8} />
                            <span className="truncate">Submitted: {formatDateTime(report.submissionDate || report.createdAt)}</span>
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                </AnimatePresence>
              </div>

              {/* Desktop Table View */}
              <div className="hidden sm:block bg-primary-light dark:bg-primary-dark rounded-lg shadow-md border border-secondary-light dark:border-secondary-dark overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-secondary-light/30 dark:bg-secondary-dark/30 border-b border-secondary-light dark:border-secondary-dark">
                      <tr>
                        <th className="text-left text-xs sm:text-sm font-medium">Type</th>
                        <th className="text-left text-xs sm:text-sm font-medium">Report ID</th>
                        <th className="text-left text-xs sm:text-sm font-medium">Trainee</th>
                        <th className="text-left text-xs sm:text-sm font-medium">Trainer</th>
                        <th className="text-left text-xs sm:text-sm font-medium">Report Date</th>
                        <th className="text-left text-xs sm:text-sm font-medium">Submitted</th>
                        <th className="text-center text-xs sm:text-sm font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      <AnimatePresence>
                        {reports.map((report, index) => {
                          const config = reportTypes[report.reportType];
                          const IconComponent = config.icon;
                          const colorClass = {
                            blue: 'text-blue-600 dark:text-blue-400',
                            green: 'text-green-600 dark:text-green-400',
                            red: 'text-red-600 dark:text-red-400'
                          }[config.color];

                          return (
                            <motion.tr
                              key={report.id}
                              initial={{ opacity: 0, y: 20 }}
                              animate={{ opacity: 1, y: 0 }}
                              exit={{ opacity: 0, x: -20 }}
                              transition={{ duration: 0.3, delay: index * 0.05 }}
                              className="border-b border-secondary-light/20 dark:border-secondary-dark/20 hover:bg-secondary-light/10 dark:hover:bg-secondary-dark/10"
                            >
                              <td className="p-2 sm:p-3">
                                <div className="flex items-center">
                                  <IconComponent className={`mr-1 ${colorClass}`} size={14} />
                                  <span className="text-xs font-medium">{report.reportType}</span>
                                </div>
                              </td>
                              <td className="p-2 sm:p-3">
                                <div className="flex items-center">
                                  <FiHash className="mr-1 text-text-light/50 dark:text-text-dark/50" size={14} />
                                  <span className="font-medium text-xs sm:text-sm">{report.reportId}</span>
                                </div>
                              </td>
                              <td className="p-2 sm:p-3">
                                <div className="text-xs sm:text-sm">
                                  <div className="font-medium">{report.traineeName || 'Unknown'}</div>
                                  {report.traineeIdNumber && (
                                    <div className="text-xs text-text-light/60 dark:text-text-dark/60">
                                      #{report.traineeIdNumber}
                                    </div>
                                  )}
                                </div>
                              </td>
                              <td className="p-2 sm:p-3">
                                <div className="text-xs sm:text-sm">{report.trainerName || 'Unknown'}</div>
                              </td>
                              <td className="p-2 sm:p-3">
                                <div className="flex items-center text-xs sm:text-sm">
                                  <FiCalendar className="mr-1 text-text-light/50 dark:text-text-dark/50" size={12} />
                                  {formatDate(report.reportDate)}
                                </div>
                              </td>
                              <td className="p-2 sm:p-3">
                                <div className="flex items-center text-xs sm:text-sm text-text-light/70 dark:text-text-dark/70">
                                  <FiClock className="mr-1" size={12} />
                                  {formatDateTime(report.submissionDate || report.createdAt)}
                                </div>
                              </td>
                              <td className="p-2 sm:p-3 text-center">
                                <motion.button
                                  whileHover={{ scale: 1.05 }}
                                  whileTap={{ scale: 0.95 }}
                                  onClick={() => deleteReport(report.id, report.reportType, report.reportId)}
                                  disabled={deletingId === report.id}
                                  className="inline-flex items-center px-2 sm:px-3 py-1 text-xs sm:text-sm rounded-md bg-red-500/10 text-red-600 dark:text-red-400 hover:bg-red-500/20 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  {deletingId === report.id ? (
                                    <>
                                      <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-red-600 dark:border-red-400 mr-2"></div>
                                      Deleting...
                                    </>
                                  ) : (
                                    <>
                                      <FiTrash2 className="mr-1" size={14} />
                                      Delete
                                    </>
                                  )}
                                </motion.button>
                              </td>
                            </motion.tr>
                          );
                        })}
                      </AnimatePresence>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Load More */}
              {Object.values(hasMore).some(Boolean) && (
                <div className="mt-4 text-center">
                  <button
                    onClick={() => loadReports(true)}
                    disabled={loading}
                    className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md bg-accent-light dark:bg-accent-dark text-primary-light dark:text-primary-dark hover:brightness-110 transition-all duration-200 disabled:opacity-50 shadow-md"
                  >
                    {loading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Loading...
                      </>
                    ) : (
                      <>
                        <FiChevronRight className="mr-2" />
                        Load More
                      </>
                    )}
                  </button>
                </div>
              )}
            </>
          )}
        </motion.div>
      </div>

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ ...confirmModal, isOpen: false })}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
      />

      {/* PIN Verification Modal */}
      {pinModal.isOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="bg-primary-light dark:bg-primary-dark rounded-lg shadow-xl border border-secondary-light dark:border-secondary-dark p-6 w-full max-w-md"
          >
            <div className="flex items-center mb-4">
              <div className="w-10 h-10 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mr-3">
                <FiAlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-text-light dark:text-text-dark">
                  Super Admin Access Required
                </h3>
             
              </div>
            </div>

            <form onSubmit={handlePinSubmit}>
              <div className="mb-4">
                
                <input
                  type="password"
                  value={pinInput}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 4);
                    setPinInput(value);
                    setPinError('');
                  }}
                  disabled={isLocked}
                  className="w-full p-3 text-center text-lg font-mono rounded-md bg-secondary-light/30 dark:bg-secondary-dark border border-secondary-light dark:border-secondary-dark focus:ring-2 focus:ring-accent-light dark:focus:ring-accent-dark outline-none transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  placeholder="Enter 4-digit PIN"
                  maxLength="4"
                  pattern="[0-9]*"
                  inputMode="numeric"
                  autoFocus
                />
                
                {/* Mobile Keypad */}
                <div className="mt-4 grid grid-cols-3 gap-2 sm:hidden">
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((digit) => (
                    <button
                      key={digit}
                      type="button"
                      onClick={() => {
                        if (pinInput.length < 4 && !isLocked) {
                          setPinInput(prev => prev + digit.toString());
                          setPinError('');
                        }
                      }}
                      disabled={isLocked || pinInput.length >= 4}
                      className="h-12 rounded-md bg-secondary-light/50 dark:bg-secondary-dark border border-secondary-light dark:border-secondary-dark text-lg font-semibold hover:bg-secondary-light dark:hover:bg-secondary-dark/80 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {digit}
                    </button>
                  ))}
                  <button
                    type="button"
                    onClick={() => {
                      setPinInput('');
                      setPinError('');
                    }}
                    disabled={isLocked}
                    className="h-12 rounded-md bg-red-500/20 border border-red-500/30 text-sm font-medium text-red-600 dark:text-red-400 hover:bg-red-500/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Clear
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      if (pinInput.length < 4 && !isLocked) {
                        setPinInput(prev => prev + '0');
                        setPinError('');
                      }
                    }}
                    disabled={isLocked || pinInput.length >= 4}
                    className="h-12 rounded-md bg-secondary-light/50 dark:bg-secondary-dark border border-secondary-light dark:border-secondary-dark text-lg font-semibold hover:bg-secondary-light dark:hover:bg-secondary-dark/80 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    0
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setPinInput(prev => prev.slice(0, -1));
                      setPinError('');
                    }}
                    disabled={isLocked || pinInput.length === 0}
                    className="h-12 rounded-md bg-yellow-500/20 border border-yellow-500/30 text-sm font-medium text-yellow-600 dark:text-yellow-400 hover:bg-yellow-500/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    ⌫
                  </button>
                </div>
                
                {pinError && (
                  <p className="mt-2 text-sm text-red-600 dark:text-red-400">
                    {pinError}
                  </p>
                )}
                {isLocked && (
                  <p className="mt-2 text-xs text-yellow-600 dark:text-yellow-400">
                    Access will be restored automatically after 15 minutes.
                  </p>
                )}
              </div>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setPinModal({ isOpen: false, onSuccess: () => {} });
                    setPinInput('');
                    setPinError('');
                  }}
                  className="flex-1 px-4 py-2 text-sm font-medium rounded-md bg-secondary-light/60 dark:bg-secondary-dark/60 text-text-light dark:text-text-dark hover:bg-secondary-light dark:hover:bg-secondary-dark transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!pinInput.trim() || isLocked}
                  className="flex-1 px-4 py-2 text-sm font-medium rounded-md bg-accent-light dark:bg-accent-dark text-primary-light dark:text-primary-dark hover:brightness-110 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Verify
                </button>
              </div>
            </form>

            {!isLocked && attempts > 0 && (
              <div className="mt-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-md">
                <p className="text-xs text-yellow-800 dark:text-yellow-200">
                  ⚠️ {attempts} failed attempt{attempts > 1 ? 's' : ''}. 
                  {maxAttempts - attempts} remaining before 15-minute lockout.
                </p>
              </div>
            )}
          </motion.div>
        </div>
      )}
    </motion.div>
  );
};

export default ManageDORs;